// Vista de ranking de pujas por usuario único
// REFACTORIZADO: Lógica en archivo principal, UI en subcomponentes
// Incluye filas expandibles con journey completo (intentos + pujas)

import React, { useState, useEffect, useMemo } from 'react';
import { Trophy, Clock, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { getPropertyBids } from '../../../services/supabaseService';
import { getPropertyBiddingAttempts } from '../../../services/supabase/engagement';
import { BidderRow, NonBidderRow, formatAmount } from './BidsRankingView/BidsRankingExports';

/**
 * Vista principal del ranking de pujas
 * Muestra tabla con pujadores, no-pujadores, y journey expandible
 */
export const BidsRankingView = ({ buyerSummaries, propertyId, isSpectatorMode = false }) => {
  // Estados de datos
  const [bids, setBids] = useState([]);
  const [biddingAttempts, setBiddingAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados de UI
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // ============================================
  // CARGA DE DATOS
  // ============================================
  
  useEffect(() => {
    loadData();
  }, [propertyId]);

  const loadData = async () => {
    if (!propertyId) return;

    setLoading(true);
    setError(null);

    try {
      // Cargar pujas confirmadas y intentos en paralelo
      const [propertyBids, attempts] = await Promise.all([
        getPropertyBids(propertyId),
        getPropertyBiddingAttempts(propertyId),
      ]);
      
      setBids(propertyBids);
      setBiddingAttempts(attempts);
    } catch (err) {
      console.error('Error cargando pujas:', err);
      setError('Error al cargar las pujas');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PROCESAMIENTO DE DATOS
  // ============================================

  // Mapear intentos por email para lookup rápido
  const attemptsByEmail = useMemo(() => {
    const map = {};
    biddingAttempts.forEach(a => {
      map[a.email] = a;
    });
    return map;
  }, [biddingAttempts]);

  // Procesar pujas para agrupar por usuario
  const processedBidders = useMemo(() => {
    if (!bids || bids.length === 0) return [];

    const bidsByEmail = {};
    
    bids.forEach(bid => {
      const email = bid.buyerEmail;
      if (!bidsByEmail[email]) {
        bidsByEmail[email] = {
          email,
          name: bid.buyerName,
          bids: [],
          maxBid: 0,
          maxBidIsLastChance: false,
          maxBidTime: null,
          maxBidConditions: [],
          maxBidConditionsText: '',
        };
      }
      
      bidsByEmail[email].bids.push(bid);
      
      if (bid.amount > bidsByEmail[email].maxBid) {
        bidsByEmail[email].maxBid = bid.amount;
        bidsByEmail[email].maxBidIsLastChance = bid.is_last_chance || false;
        bidsByEmail[email].maxBidTime = bid.timestamp;
        bidsByEmail[email].maxBidConditions = bid.selectedConditions || [];
        bidsByEmail[email].maxBidConditionsText = bid.conditionsText || '';
      }
    });

    // Ordenar pujas de cada usuario por monto
    Object.values(bidsByEmail).forEach(bidder => {
      bidder.bids.sort((a, b) => a.amount - b.amount || new Date(a.timestamp) - new Date(b.timestamp));
    });

    return Object.values(bidsByEmail).sort((a, b) => b.maxBid - a.maxBid || new Date(a.maxBidTime) - new Date(b.maxBidTime));
  }, [bids]);

  // Obtener usuarios que NO han pujado
  const nonBidders = useMemo(() => {
    if (!buyerSummaries) return [];
    
    const biddersEmails = new Set(processedBidders.map(b => b.email));
    return buyerSummaries.filter(buyer => !biddersEmails.has(buyer.email));
  }, [buyerSummaries, processedBidders]);

  // Estadísticas
  const topBid = processedBidders.length > 0 ? processedBidders[0].maxBid : 0;
  const totalBids = bids.length;
  const totalBidders = processedBidders.length;
  const lastChanceBids = bids.filter(b => b.is_last_chance).length;
  const totalWithAttempts = biddingAttempts.length;

  // ============================================
  // HANDLERS
  // ============================================

  const toggleRow = (email) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      const allEmails = [
        ...processedBidders.map(b => b.email),
        ...nonBidders.filter(b => attemptsByEmail[b.email]).map(b => b.email),
      ];
      setExpandedRows(new Set(allEmails));
    }
    setAllExpanded(!allExpanded);
  };

  // ============================================
  // RENDER: ESTADOS DE CARGA/ERROR/VACÍO
  // ============================================

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-4"></div>
        <p className="text-slate-400">Cargando pujas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-center">
        <p className="text-red-400">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 text-sm text-red-400 hover:text-red-300 underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Vista cuando no hay pujas pero puede haber intentos
  if (bids.length === 0 && biddingAttempts.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700">
        <Trophy className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-slate-400 mb-2">
          Sin pujas aún
        </h3>
        <p className="text-slate-500">
          Esta propiedad aún no ha recibido ninguna puja ni intento
        </p>
      </div>
    );
  }

  // ============================================
  // RENDER PRINCIPAL
  // ============================================

  return (
    <div className="space-y-6">
      {/* Header con estadísticas */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-400" />
          Ranking de Pujas
        </h3>
        
        <div className="flex gap-3 text-sm flex-wrap">
          <div className="bg-slate-800/50 rounded-lg px-3 py-1.5 border border-slate-700">
            <span className="text-slate-400">Pujas:</span>
            <span className="text-white font-semibold ml-2">{totalBids}</span>
          </div>
          <div className="bg-slate-800/50 rounded-lg px-3 py-1.5 border border-slate-700">
            <span className="text-slate-400">Pujadores:</span>
            <span className="text-white font-semibold ml-2">{totalBidders}</span>
          </div>
          {totalWithAttempts > 0 && (
            <div className="bg-orange-500/10 rounded-lg px-3 py-1.5 border border-orange-500/30">
              <span className="text-orange-400">Con intentos:</span>
              <span className="text-orange-300 font-semibold ml-2">{totalWithAttempts}</span>
            </div>
          )}
          {lastChanceBids > 0 && (
            <div className="bg-amber-500/10 rounded-lg px-3 py-1.5 border border-amber-500/30">
              <span className="text-amber-400">Última oportunidad:</span>
              <span className="text-amber-300 font-semibold ml-2">{lastChanceBids}</span>
            </div>
          )}
        </div>
      </div>

      {/* Botón expandir/colapsar todos */}
      {(processedBidders.length > 0 || nonBidders.some(b => attemptsByEmail[b.email])) && (
        <div className="flex justify-end">
          <button
            onClick={toggleAllRows}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
          >
            {allExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Colapsar todos
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Expandir todos
              </>
            )}
          </button>
        </div>
      )}

      {/* Tabla de ranking */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700 text-left">
              <th className="py-3 px-2 w-8"></th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-12">#</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Usuario</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Puja máxima</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center w-20">Intentos</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center w-20">
                <Clock className="w-4 h-4 inline-block" title="Última oportunidad" />
              </th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha/Hora</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center w-20">Cond.</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">vs Líder</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center w-16"># Pujas</th>
              <th className="py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-12"></th>
            </tr>
          </thead>
          <tbody>
            {/* Usuarios que han pujado */}
            {processedBidders.map((bidder, index) => (
              <BidderRow
                key={bidder.email}
                bidder={bidder}
                rank={index + 1}
                topBid={topBid}
                isWinner={index === 0}
                journey={attemptsByEmail[bidder.email]?.attempts || []}
                propertyId={propertyId}
                buyerSummary={buyerSummaries?.find(b => b.email === bidder.email)}
                isExpanded={expandedRows.has(bidder.email)}
                onToggleExpand={() => toggleRow(bidder.email)}
                isSpectatorMode={isSpectatorMode}
              />
            ))}

            {/* Separador si hay usuarios sin pujas */}
            {nonBidders.length > 0 && (
              <tr>
                <td colSpan={11} className="py-4">
                  <div className="flex items-center gap-3 text-slate-500">
                    <div className="flex-1 border-t border-slate-700"></div>
                    <span className="text-xs flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Invitados sin pujas ({nonBidders.length})
                    </span>
                    <div className="flex-1 border-t border-slate-700"></div>
                  </div>
                </td>
              </tr>
            )}

            {/* Usuarios que NO han pujado */}
            {nonBidders.map(buyer => (
              <NonBidderRow
                key={buyer.email}
                buyer={buyer}
                journey={attemptsByEmail[buyer.email]?.attempts || []}
                propertyId={propertyId}
                isExpanded={expandedRows.has(buyer.email)}
                onToggleExpand={() => toggleRow(buyer.email)}
                isSpectatorMode={isSpectatorMode}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-6 text-xs text-slate-500 pt-4 border-t border-slate-700 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-green-400">✅</span>
          <span>Puja confirmada</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-red-400">🔴</span>
          <span>Abandonado</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-red-300">❌</span>
          <span>Error validación</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-yellow-400">📧</span>
          <span>Pendiente verificar</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-orange-400">⏰</span>
          <span>Expirado</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded text-xs">€XXX</span>
          <span>Puja en última oportunidad</span>
        </div>
      </div>
    </div>
  );
};

export default BidsRankingView;